import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import IconInit from "./helpers/IconInit";
import RouterScrollToTop from "./helpers/RouterScrollToTop";
import HomePage from "./pages/HomePage";
// import "../public/assets/css/slick.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// import ShopPage from "./pages/ShopPage";
import ContactPage from "./pages/ContactPage";
import DynamicProductDetail from "./pages/DynamicProductDetail";
import WishlistPage from "./pages/WishlistPage";

import Auth from "./pages/Auth";
import CartCheckoutPage from "./pages/CartCheckOutPage";
import Tracking from "./pages/Tracking";
import { SplashScreen } from "./layout/SplashScreen/SplashScreen";
// Initialize security middleware
import "./utils/securityInit";
import ProductPage from "./pages/ProductPage";

import UpdatePassword from "./pages/UpdatePassword";
import ForgetPassword from "./pages/ForgetPassword";
import { useAuthStore } from "./store/authStore";
import { useWishlistStore } from "./store/wishlistStore";
import { useCartStore } from "./store/cartStore";
import { getUserCardGetV2, addProductToCart } from "./hooks/useSupabase";

/** Initializes auth listener and syncs wishlist/cart with auth state. */
const StoreInitializer: React.FC = () => {
  const initialize = useAuthStore((s) => s.initialize);
  const userId = useAuthStore((s) => s.userId);
  const fetchWishlist = useWishlistStore((s) => s.fetchWishlist);
  const resetWishlist = useWishlistStore((s) => s.reset);
  const clearCart = useCartStore((s) => s.clearCart);
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const cleanup = initialize();
    return cleanup;
  }, []);

  useEffect(() => {
    // Skip on first render before auth resolves
    if (prevUserIdRef.current === undefined) {
      prevUserIdRef.current = userId;
      return;
    }
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = userId;

    if (userId) {
      // Logged in (refresh or fresh login) — merge guest cart with DB cart.
      // Snapshot guest items BEFORE the fetch so concurrent DB writes don't race.
      const guestItems = useCartStore.getState().items;
      (async () => {
        try {
          const res: any = await getUserCardGetV2({});
          const dbItems: any[] = res?.data?.items ?? [];

          const keyOf = (pid: number, vid?: number | null) =>
            `${pid}|${vid ?? "null"}`;

          type Merged = {
            product_id: number;
            variant_id?: number;
            quantity: number;
            dbQty: number;
            product: any;
          };
          const merged = new Map<string, Merged>();

          for (const it of dbItems) {
            const vid = it.product_variant_id ?? undefined;
            merged.set(keyOf(it.product_id, vid), {
              product_id: it.product_id,
              variant_id: vid,
              quantity: it.qty,
              dbQty: it.qty,
              product: {
                id: it.product_id,
                price: it.usd_price ?? null,
                discount: it.sale_rate != null ? it.sale_rate / 100 : null,
                name: it.name,
              },
            });
          }

          for (const g of guestItems) {
            const k = keyOf(g.product_id, g.variant_id);
            const existing = merged.get(k);
            if (existing) {
              existing.quantity = Math.max(existing.quantity, g.quantity);
              // Prefer guest product (richer fields like is_unlimited_stock).
              existing.product = { ...existing.product, ...g.product };
            } else {
              merged.set(k, {
                product_id: g.product_id,
                variant_id: g.variant_id,
                quantity: g.quantity,
                dbQty: 0,
                product: g.product,
              });
            }
          }

          // Push items where merged qty > db qty.
          await Promise.all(
            Array.from(merged.values())
              .filter((m) => m.quantity > m.dbQty)
              .map((m) =>
                addProductToCart({
                  productId: m.product_id,
                  quantity: m.quantity,
                  variantId: m.variant_id,
                }).catch(() => {}),
              ),
          );

          // Apply merged state to local store directly (don't call syncFromDB
          // with raw DB items — that would discard guest additions).
          useCartStore.setState({
            items: Array.from(merged.values()).map((m) => ({
              id: m.product_id,
              product_id: m.product_id,
              quantity: m.quantity,
              product: m.product,
              variant_id: m.variant_id,
            })),
            error: null,
          });
        } catch {
          // best-effort; leave local cart intact on failure
        }
      })();
      setTimeout(() => fetchWishlist(), 100);
    } else if (prev) {
      // Explicitly logged out — clear wishlist and cart
      resetWishlist();
      clearCart();
    }
  }, [userId]);

  return null;
};

const App: React.FC = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [splashLoaded, setSplashLoaded] = useState(false);

  useEffect(() => {
    // Always show splash screen for demo purposes
    setSplashLoaded(true);
  }, []);

  const handleSplashComplete = () => {
    setShowSplash(false);
    localStorage.setItem("hasSeenSplash", "true");
  };

  if (!splashLoaded) {
    return null; // Prevent flash of content
  }
  return (
    <>
      <Toaster position="bottom-center" />
      <StoreInitializer />
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <BrowserRouter>
        <RouterScrollToTop />
        <IconInit />

        <div className="min-h-screen flex flex-col">
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              {/* <Route path="shop" element={<ShopPage />} /> */}
              <Route path="contact" element={<ContactPage />} />
              <Route
                path="product/:slug"
                element={<DynamicProductDetail />}
              />
              <Route path="cart" element={<CartCheckoutPage />} />
              <Route path="products" element={<ProductPage />} />
              <Route path="tracking" element={<Tracking />} />
              <Route path="wishlist" element={<WishlistPage />} />
              {/* <Route path="signin" element={<SignIn />} /> */}

              <Route path="registration" element={<Auth />} />
              <Route path="forget-password" element={<ForgetPassword />} />
              <Route path="reset-password" element={<UpdatePassword />} />
              <Route path="*" element={<div>404 Not Found</div>} />
            </Routes>
          </main>
        </div>
      </BrowserRouter>
      {/* React Query DevTools - only in development */}
      {process.env.NODE_ENV === "development" && <ReactQueryDevtools />}
    </>
  );
};

export default App;
